import { track, analyticsKeys } from '@mobi/account/Utils'
import { DepositLimitFrequency } from '@mobi/api-types'

export type AccountManagementEvent = {
  interaction_type:
    | 'Set Deposit Limit'
    | 'Edit Deposit Limit'
    | 'Deposit Limit Pop Up Displayed'
    | 'Keep Deposit Limit'
    | 'Remove Bet Limit'
    | 'Set Bet Limit'
    | 'Need Assistance'
    | 'Block Debit Card'
    | 'Unblock Debit Card'
    | 'Cancel Unblock Debit Card'
    | 'Bet Limit Error'
    | 'Deposit Limit Error'
  limitAmount: string | undefined
  location: 'Bet Limits' | 'Deposit Limit' | 'Deposit Limit Pop Up' | undefined
  depositFrequency: DepositLimitFrequency | 'None' | undefined
  message: string | undefined
}

const defaultAccountManagementValues: Omit<AccountManagementEvent, 'interaction_type'> = {
  limitAmount: undefined,
  location: undefined,
  depositFrequency: undefined,
  message: undefined,
}

export const accountManagementTracker = (event: AccountManagementEvent) =>
  track(analyticsKeys.accountManagement, event)

/*
 * Bet Limit
 */

export const trackRemoveBetLimit = () =>
  accountManagementTracker({
    interaction_type: 'Remove Bet Limit',
    ...defaultAccountManagementValues,
  })

export const trackSetBetLimit = (limitAmount: string) =>
  accountManagementTracker({
    interaction_type: 'Set Bet Limit',
    ...defaultAccountManagementValues,
    limitAmount,
  })

export const trackNeedAssistance = (location: 'Bet Limits' | 'Deposit Limit') =>
  accountManagementTracker({
    interaction_type: 'Need Assistance',
    ...defaultAccountManagementValues,
    location: location,
  })

export const trackBetLimitError = (message: string) =>
  accountManagementTracker({
    interaction_type: 'Bet Limit Error',
    ...defaultAccountManagementValues,
    location: 'Bet Limits',
    message: message,
  })

/*
 * Deposit Limit
 */

export const trackSetDepositLimit = (
  depositFrequency: DepositLimitFrequency | undefined,
  limitAmount: string | undefined
) =>
  accountManagementTracker({
    interaction_type: 'Set Deposit Limit',
    ...defaultAccountManagementValues,
    depositFrequency: depositFrequency ?? 'None',
    limitAmount,
  })

export const trackEditDepositLimit = () =>
  accountManagementTracker({
    interaction_type: 'Edit Deposit Limit',
    ...defaultAccountManagementValues,
    location: 'Deposit Limit Pop Up',
  })

export const trackDepositLimitPopUpDisplayed = () =>
  accountManagementTracker({
    interaction_type: 'Deposit Limit Pop Up Displayed',
    ...defaultAccountManagementValues,
  })

export const trackKeepDepositLimit = () =>
  accountManagementTracker({
    interaction_type: 'Keep Deposit Limit',
    ...defaultAccountManagementValues,
    location: 'Deposit Limit Pop Up',
  })

export const trackDepositLimitError = (message: string) =>
  accountManagementTracker({
    interaction_type: 'Deposit Limit Error',
    ...defaultAccountManagementValues,
    location: 'Deposit Limit',
    message: message,
  })

/*
 * Block Debit Card
 */

export const trackBlockDebitCard = () =>
  accountManagementTracker({
    interaction_type: 'Block Debit Card',
    ...defaultAccountManagementValues,
  })

export const trackUnblockDebitCard = () =>
  accountManagementTracker({
    interaction_type: 'Unblock Debit Card',
    ...defaultAccountManagementValues,
  })

export const trackCancelUnblockDebitCard = () =>
  accountManagementTracker({
    interaction_type: 'Cancel Unblock Debit Card',
    ...defaultAccountManagementValues,
  })
